import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import translations from '../translations.js';

/**
 * Admin/Analytics localization. Reuses the app's convention: keys are the English
 * strings, language is the shared sessionStorage 'langOpt' (so it stays in sync
 * with the chatbot's Language dropdown). Only English + Japanese are offered here.
 * English falls back to the key itself; Japanese uses ADMIN_STRINGS, then the
 * shared translations.js, then the key.
 */
export const ADMIN_STRINGS = {
  // Shell / nav
  'Admin Console': { jp: '管理コンソール' },
  'Users': { jp: 'ユーザー' },
  'Analytics': { jp: '分析' },
  'Back to Chat': { jp: 'チャットに戻る' },
  'User Management': { jp: 'ユーザー管理' },

  // Access states
  'Checking access…': { jp: 'アクセスを確認中…' },
  'Access denied': { jp: 'アクセスが拒否されました' },
  'You need admin rights to view this page.': { jp: 'このページを表示するには管理者権限が必要です。' },
  'You need admin rights to view analytics.': { jp: '分析を表示するには管理者権限が必要です。' },
  'Loading…': { jp: '読み込み中…' },

  // Users page
  'Total Users': { jp: '総ユーザー数' },
  'Admins': { jp: '管理者' },
  'Active': { jp: 'アクティブ' },
  'Edit Access': { jp: '編集権限' },
  'Provision a user': { jp: 'ユーザーを追加' },
  'Email or Business ID (short id)': { jp: 'メールまたはビジネスID（短縮ID）' },
  'e.g. abbiswa or jane@corp.com': { jp: '例：abbiswa または jane@corp.com' },
  'Make admin': { jp: '管理者にする' },
  'Edit access': { jp: '編集権限' },
  'Add user': { jp: 'ユーザーを追加' },
  'Adding…': { jp: '追加中…' },
  'All users': { jp: 'すべてのユーザー' },
  'Search': { jp: '検索' },
  'Clear': { jp: 'クリア' },
  'Search by email or id': { jp: 'メールまたはIDで検索' },
  'No users found.': { jp: 'ユーザーが見つかりません。' },
  'User': { jp: 'ユーザー' },
  'Type': { jp: '種別' },
  'Admin': { jp: '管理者' },
  'Access': { jp: 'アクセス' },
  'email': { jp: 'メール' },
  'short id': { jp: '短縮ID' },
  'edit': { jp: '編集' },
  'read-only': { jp: '読み取り専用' },
  'active': { jp: 'アクティブ' },
  'disabled': { jp: '無効' },
  'you': { jp: 'あなた' },
  'Failed to load users.': { jp: 'ユーザーの読み込みに失敗しました。' },
  'Update failed.': { jp: '更新に失敗しました。' },
  'Could not add user.': { jp: 'ユーザーを追加できませんでした。' },

  // Analytics — filters
  'Today': { jp: '今日' },
  'Last 7 days': { jp: '過去7日間' },
  'Last 30 days': { jp: '過去30日間' },
  'This month': { jp: '今月' },
  'This year': { jp: '今年' },
  'All time': { jp: '全期間' },
  'From': { jp: '開始日' },
  'To': { jp: '終了日' },
  'Apply filter': { jp: 'フィルターを適用' },
  'Reset': { jp: 'リセット' },

  // Analytics — KPIs
  'Total Chats': { jp: '総チャット数' },
  'Total Messages': { jp: '総メッセージ数' },
  'Active Users': { jp: 'アクティブユーザー' },
  'Registered Users': { jp: '登録ユーザー' },
  'Message Feedback': { jp: 'メッセージフィードバック' },
  'App Rating': { jp: 'アプリ評価' },
  'avg / chat': { jp: '平均 / チャット' },
  'in selected range': { jp: '選択した期間内' },
  'admins': { jp: '管理者' },
  'ratings': { jp: '件の評価' },

  // Analytics — charts
  'Chat share by user': { jp: 'ユーザー別チャット割合' },
  'App rating distribution': { jp: 'アプリ評価の分布' },
  'Message votes': { jp: 'メッセージ投票' },
  'Monthly activity': { jp: '月間アクティビティ' },
  'Recent feedback': { jp: '最近のフィードバック' },
  'Chats': { jp: 'チャット' },
  'Messages': { jp: 'メッセージ' },
  'chats': { jp: 'チャット' },
  'votes': { jp: '投票' },
  'total': { jp: '合計' },
  'Vote': { jp: '投票' },
  'Comment': { jp: 'コメント' },
  'When': { jp: '日時' },
  'Others': { jp: 'その他' },
  'No data to chart.': { jp: '表示するデータがありません。' },
  'No data for this range.': { jp: 'この期間のデータはありません。' },
  'No feedback in this range yet.': { jp: 'この期間のフィードバックはまだありません。' },
  'Failed to load analytics.': { jp: '分析の読み込みに失敗しました。' },
};

export function translateAdmin(lang, key) {
  if (key == null) return key;
  const isJp = lang === 'Japanese';
  const a = ADMIN_STRINGS[key];
  if (isJp) {
    if (a && a.jp) return a.jp;
    const t = translations[key];
    if (t && t.translationJp) return t.translationJp;
    return key;
  }
  if (a && a.en) return a.en;
  const t = translations[key];
  if (t && t.translationEng) return t.translationEng;
  return key; // English == the key
}

const AdminI18nContext = createContext(null);

export function AdminI18nProvider({ children }) {
  const [lang, setLangState] = useState(() => sessionStorage.getItem('langOpt') || 'Japanese');
  const setLang = useCallback((l) => {
    sessionStorage.setItem('langOpt', l);
    setLangState(l);
  }, []);
  const t = useCallback((key) => translateAdmin(lang, key), [lang]);
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <AdminI18nContext.Provider value={value}>{children}</AdminI18nContext.Provider>;
}

export function useAdminI18n() {
  const ctx = useContext(AdminI18nContext);
  if (ctx) return ctx;
  // Safe fallback if a component renders outside the provider.
  const lang = sessionStorage.getItem('langOpt') || 'Japanese';
  return { lang, setLang: () => {}, t: (k) => translateAdmin(lang, k) };
}
